import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { urlHtmlRelatorio, urlPdfRelatorio, razaoContas, criarSnapshot, obterLancamento, atualizarLancamento, planoCliente, excluirLancamento, obterPreferencias, salvarPreferencias } from '../api';
import { PageHeader, Empty, Loading, Alert } from '../components/common';
import { AvisoForaDaCarteira } from '../components/CompetenciaFinal';
import { mesAtual, anosOpcoes, mesOpcoes, fmtMoeda, fmtData } from '../lib/format';

const API_HOST = import.meta.env.VITE_API_URL || 'http://127.0.0.1:3050';

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const DEV_USER = import.meta.env.VITE_DEV_USER || 'contato@atriocontabilidade.com.br';
import { authHeaders } from '../lib/auth-headers';

/**
 * Carrega HTML do relatório via fetch autenticado.
 * Iframes não permitem custom headers no src, então buscamos manualmente
 * e injetamos via srcDoc — preserva isolamento de estilos (CSS do relatório
 * não vaza pro shell e vice-versa).
 */
async function carregarHtmlAutenticado(url) {
  const resp = await fetch(url, {
    headers: authHeaders(),
  });
  if (!resp.ok) {
    const txt = await resp.text();
    throw new Error(`HTTP ${resp.status}: ${txt.slice(0, 200)}`);
  }
  return await resp.text();
}

const RELATORIOS = [
  { id: 'dre',           label: 'DRE',                    icon: '💹' },
  { id: 'dre-mensal',    label: 'DRE Mensal (12m)',       icon: '📅' },
  { id: 'bp-mensal',     label: 'BP Mensal (12m)',        icon: '🗓' },
  { id: 'balancete-mensal', label: 'Balancete Mensal (12m)', icon: '📑' },
  { id: 'balanco',       label: 'Balanço Patrimonial',    icon: '⚖' },
  { id: 'balancete',     label: 'Balancete',              icon: '📒' },
  { id: 'razao',         label: 'Razão',                  icon: '📖' },
  { id: 'composicao',    label: 'Composições',             icon: '🧾' },
  { id: 'faturamento12', label: 'Faturamento 12 meses',   icon: '📊' },
];

// Snapshot (congelar versão) — só os tipos que o backend persiste
const SNAPSHOT_TIPO = { dre: 'DRE', balanco: 'BP', balancete: 'BALANCETE', faturamento12: 'FAT12' };

// Relatórios que NÃO suportam comparação (faturamento12/dre-mensal já são séries)
const SEM_COMPARACAO = new Set(['faturamento12', 'balancete', 'razao', 'dre-mensal', 'bp-mensal',
                                'balancete-mensal', 'composicao']);

// Composicao: os grupos de terceiros, na ordem em que o contador olha.
// Vazio traz todos os que existirem no plano do cliente.
const GRUPOS_COMPOSICAO = [
  { value: 'clientes',     label: 'Contas a receber' },
  { value: 'fornecedores', label: 'Fornecedores' },
  { value: 'adiant_cli',   label: 'Adiantamentos de clientes' },
  { value: 'adiant_forn',  label: 'Adiantamentos a fornecedores' },
  { value: 'adiant_socio', label: 'Adiantamentos a sócios' },
];
// Relatórios que NÃO suportam seletor de tipo (mensal/acumulado/anual)
const SEM_TIPO = new Set(['faturamento12', 'dre-mensal', 'bp-mensal', 'balancete-mensal']);

const TIPOS = [
  { value: 'mensal',          label: 'Mensal' },
  { value: 'trimestral',      label: 'Trimestral' },
  { value: 'semestral',       label: 'Semestral' },
  { value: 'acumulado_ano',   label: 'Acumulado no ano' },
  // Janela movel: so na DRE — 'ultimos 12 meses' nao diz nada de um balanco.
  { value: 'ultimos_12',      label: 'Últimos 12 meses', soDre: true },
  { value: 'anual',           label: 'Anual' },
  { value: 'livre',           label: 'Período livre (data a data)' },
];

export default function Relatorios({ clienteId }) {
  const comp = mesAtual();
  const [ano, mes] = comp.split('-').map(Number);

  const [rel, setRel] = useState(() => {
    try {
      const t = sessionStorage.getItem('flow_relatorios_tab');
      if (t) { sessionStorage.removeItem('flow_relatorios_tab'); return t; }
    } catch {}
    return 'dre';
  });
  const [filtroAno, setFiltroAno] = useState(ano);
  const [filtroMes, setFiltroMes] = useState(mes);
  const [tipo, setTipo] = useState('mensal');
  const [comparar, setComparar] = useState('');
  const [modoBp, setModoBp] = useState('saldo');
  const [linhasModo, setLinhasModo] = useState('movimento');
  const [pctMensal, setPctMensal] = useState('nao');   // saldo | movimento (BP mensal)
  // '' usa o padrao do relatorio: DRE sem encerramento, balanco com ele
  const [encerramento, setEncerramento] = useState('');
  // Opcoes de impressao do balancete: codigo enxuto e assinatura unica.
  // Vivem num popover proprio para nao inchar a barra de filtros.
  const [codigoModo, setCodigoModo] = useState('completo');
  const [assinaturas, setAssinaturas] = useState('ambas');
  const [origemCol, setOrigemCol] = useState('nao');   // coluna Origem do Razão: oculta de fábrica
  // AV%/AH% na TELA: ferramenta do analista. Ligado de fábrica. Nunca vai
  // para o papel — o renderer esconde as colunas em @media print.
  const [percentuaisModo, setPercentuaisModo] = useState('sim');
  // Popovers fecham ao clicar fora: refs dos dois invólucros.
  const pacoteRef = useRef(null);
  const opcoesRef = useRef(null);
  const [opcoesAberto, setOpcoesAberto] = useState(false);
  const [gerandoPacote, setGerandoPacote] = useState(false);
  // Zoom do documento. Fica no navegador de quem esta olhando: e preferencia
  // de leitura, nao de emissao — nao muda o PDF nem o que o cliente recebe.
  const [zoom, setZoom] = useState(() => {
    try { return Number(localStorage.getItem('flow_zoom_relatorio')) || 100; }
    catch { return 100; }
  });
  // 'largura' recalcula o zoom para a folha encher o espaco; 'manual' respeita
  // o que a pessoa escolheu. Padrao de quem nunca mexeu: 'largura'.
  const [zoomModo, setZoomModo] = useState(() => {
    try { return localStorage.getItem('flow_zoom_modo') || 'largura'; }
    catch { return 'largura'; }
  });
  const molduraRef = useRef(null);

  const gravarModo = (m) => {
    setZoomModo(m);
    try { localStorage.setItem('flow_zoom_modo', m); } catch {}
  };
  const ajustarZoom = (v) => {
    const z = Math.min(200, Math.max(50, Math.round(v)));
    setZoom(z);
    gravarModo('manual');
    try { localStorage.setItem('flow_zoom_relatorio', String(z)); } catch {}
  };

  // Padrao salvo pelo usuario (independe do cliente). null = nunca salvou.
  const [prefs, setPrefs] = useState(null);
  const [padraoSalvo, setPadraoSalvo] = useState(false);

  // Pacote do cliente: escolha o que vai no documento unico
  const [pacoteAberto, setPacoteAberto] = useState(false);
  const [pacoteSel, setPacoteSel] = useState(['balancete', 'dre', 'balanco', 'razao', 'notas', 'distribuicoes']);
  // Quais opcoes de impressao cada relatorio oferece. O botao so aparece
  // quando ha alguma, e mostra quantas estao fora do padrao - a escolha nao
  // pode ficar escondida dentro do popover.
  const BTN_ZOOM = { padding: '6px 9px', minWidth: 30, lineHeight: 1, fontSize: 13 };

/**
 * Aplica o zoom no proprio HTML do relatorio.
 *
 * O documento vive num <iframe srcDoc>: o CSS do pai nao alcanca o conteudo, e
 * escalar o <iframe> por fora encolheria a area de rolagem junto. Injetando um
 * <style> no <head> do documento, quem escala e o documento — a barra de
 * rolagem continua sendo a dele e nada e requisitado de novo.
 */
function comZoom(html, zoom) {
  if (!html || zoom === 100) return html;
  // @media screen: sem isto o zoom vai junto para o papel — o documento sai
  // maior que o A4 e o navegador encolhe tudo para caber.
  const estilo = `<style>@media screen { html { zoom: ${zoom / 100}; } }</style>`;
  return html.includes('</head>')
    ? html.replace('</head>', estilo + '</head>')
    : estilo + html;
}

const COM_ASSINATURA = new Set(['balancete', 'dre', 'balanco', 'faturamento12', 'composicao']);
// Só estes têm colunas de análise. Balancete, razão e faturamento não têm.
const COM_PERCENTUAIS = new Set(['dre', 'balanco']);
// Comparativos mensais: dao a opcao de esconder a conta zerada em todos os meses.
const COM_LINHAS_VAZIAS = new Set(['dre-mensal', 'bp-mensal', 'balancete-mensal']);
  const TEM_OPCOES = { balancete: true, razao: true, dre: true, balanco: true, faturamento12: true };
  // O padrao contra o qual se mede "fora do padrao": o do usuario, se ele
  // salvou um; senao o de fabrica.
  const PADRAO = {
    codigo:      prefs?.impressao?.codigo      || 'completo',
    assinaturas: prefs?.impressao?.assinaturas || 'ambas',
    origem:      prefs?.impressao?.origem      || 'nao',
  };
  const OPCOES_ATIVAS = () =>
    (rel === 'balancete' ? (codigoModo !== PADRAO.codigo ? 1 : 0) : 0)
    + (COM_ASSINATURA.has(rel) ? (assinaturas !== PADRAO.assinaturas ? 1 : 0) : 0)
    + (rel === 'razao' ? (origemCol !== PADRAO.origem ? 1 : 0) : 0);
  const RESTAURAR_PADRAO = () => {
    setCodigoModo(PADRAO.codigo); setAssinaturas(PADRAO.assinaturas); setOrigemCol(PADRAO.origem);
  };

  // Clicar fora fecha os popovers. Sem isto só fechavam no próprio botão.
  useEffect(() => {
    const fecha = (e) => {
      if (pacoteRef.current && !pacoteRef.current.contains(e.target)) setPacoteAberto(false);
      if (opcoesRef.current && !opcoesRef.current.contains(e.target)) setOpcoesAberto(false);
    };
    document.addEventListener('mousedown', fecha);
    return () => document.removeEventListener('mousedown', fecha);
  }, []);

  // Carrega o padrao do usuario uma vez, ao abrir a tela
  useEffect(() => {
    obterPreferencias().then(p => {
      if (!p || typeof p !== 'object') return;
      setPrefs(p);
      const i = p.impressao || {};
      if (i.codigo)      setCodigoModo(i.codigo);
      if (i.assinaturas) setAssinaturas(i.assinaturas);
      if (i.origem)      setOrigemCol(i.origem);
      if (p.analise?.percentuais) setPercentuaisModo(p.analise.percentuais);
      if (Array.isArray(p.pacote) && p.pacote.length) setPacoteSel(p.pacote);
    }).catch(() => { /* sem preferencia salva: segue o de fabrica */ });
  }, []);

  // Salva o estado atual (impressao e pacote) como padrao do usuario
  const salvarPadrao = () => {
    const dados = {
      impressao: { codigo: codigoModo, assinaturas, origem: origemCol },
      pacote: pacoteSel,
      analise: { percentuais: percentuaisModo },
    };
    salvarPreferencias(dados)
      .then(p => { setPrefs(p || dados); setPadraoSalvo(true); setTimeout(() => setPadraoSalvo(false), 2500); })
      .catch(e => setErro('Não foi possível salvar o padrão: ' + e.message));
  };

  const PACOTE_ITENS = [
    ['balancete', 'Balancete de verifica\u00e7\u00e3o'],
    ['dre', 'Demonstra\u00e7\u00e3o do resultado'],
    ['balanco', 'Balan\u00e7o patrimonial'],
    ['razao', 'Raz\u00e3o anal\u00edtico'],
    ['composicao', 'Composi\u00e7\u00e3o de saldos'],
    ['notas', 'Notas fiscais escrituradas'],
    ['distribuicoes', 'Distribui\u00e7\u00f5es de lucros'],
  ];
  // Pagina de espera da aba do pacote. Fica no ar enquanto o servidor monta o
  // documento e e substituida por ele. Sem framework nem fonte externa: tem de
  // pintar imediatamente.
  const paginaEspera = (itens) => `<!DOCTYPE html><html lang="pt-BR"><head>
<meta charset="UTF-8"><title>Gerando o pacote…</title><style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{min-height:100vh;display:flex;align-items:center;justify-content:center;
       background:#F2EDE9;color:#1A1714;
       font-family:'Manrope','Segoe UI',system-ui,sans-serif}
  .cx{text-align:center;max-width:420px;padding:32px}
  .marca{color:#8A6244;letter-spacing:.34em;font-size:15px;font-weight:600}
  .marca small{display:block;letter-spacing:.18em;font-size:8px;color:#8A847D;margin-top:5px}
  .anel{width:38px;height:38px;margin:30px auto 20px;border-radius:50%;
        border:2.5px solid rgba(138,98,68,.18);border-top-color:#C4956A;
        animation:gira .9s linear infinite}
  @keyframes gira{to{transform:rotate(360deg)}}
  h1{font-size:16px;font-weight:600;margin-bottom:6px}
  p{font-size:13px;color:#5A554E;line-height:1.6}
  ul{list-style:none;margin:18px auto 0;font-size:12.5px;color:#5A554E;
     text-align:left;display:inline-block}
  li{padding:3px 0}
  li::before{content:'·';color:#C4956A;font-weight:700;margin-right:8px}
  .obs{margin-top:22px;font-size:11.5px;color:#8A847D}
</style></head><body><div class="cx">
  <div class="marca">ATRIO<small>Contabilidade</small></div>
  <div class="anel"></div>
  <h1>Montando o documento</h1>
  <p>Estamos apurando os saldos e gerando as demonstrações. Pode levar alguns
     segundos.</p>
  <ul>${itens.map(i => `<li>${i}</li>`).join('')}</ul>
  <p class="obs">Esta aba se atualiza sozinha quando ficar pronto.</p>
</div></body></html>`;

  const montarQueryPacote = () => {
    const p = new URLSearchParams({ ano: filtroAno, mes: filtroMes, tipo, incluir: pacoteSel.join(',') });
    if (codigoModo !== 'completo') p.set('codigo', codigoModo);
    if (assinaturas !== 'ambas') p.set('assinaturas', assinaturas);
    p.set('origem', origemCol);
    // O pacote nunca leva AV%/AH%: é documento, não análise.
    if (tipo === 'livre') { p.set('data_inicio', dtIni); p.set('data_fim', dtFim); }
    return p;
  };

  const baixarPacotePdf = () => {
    if (!clienteId) return;
    const p = montarQueryPacote();
    baixarArquivo(`${API_HOST}/relatorios/pdf/pacote/${clienteId}?${p}`,
      `pacote-${clienteId}-${filtroAno}-${String(filtroMes).padStart(2, '0')}.pdf`);
  };

  const abrirPacote = () => {
    const p = montarQueryPacote();

    // Aberta aqui, no gesto do usuario: dentro do .then() o navegador trata
    // como pop-up nao solicitado e bloqueia.
    const rotulos = PACOTE_ITENS.filter(([k]) => pacoteSel.includes(k)).map(([, r]) => r);
    const w = window.open('', '_blank');
    if (w) { w.document.write(paginaEspera(rotulos)); w.document.close(); }

    setGerandoPacote(true);
    setErro(null);
    fetch(`${API_HOST}/relatorios/pacote/${clienteId}/html?${p}`, { headers: authHeaders() })
      .then(r => r.ok ? r.text() : r.text().then(t => Promise.reject(new Error(t.slice(0, 200)))))
      .then(html => {
        // Blob e nao document.write: documento escrito a mao numa janela
        // aberta em branco nao tem entrada de navegacao, e o Firefox ignora o
        // window.print() dele em silencio. O botao parecia quebrado. Com o
        // blob a aba recebe um documento comum, que imprime como qualquer um.
        if (w) {
          const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
          w.location.replace(url);
          // Revogar na hora cancelaria a navegacao que acabou de comecar.
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        }
        setPacoteAberto(false);
      })
      .catch(e => {
        const msg = 'Falha ao gerar o pacote: ' + e.message;
        setErro(msg);
        // O erro tambem vai para a aba: quem esta olhando para ela precisa
        // saber, em vez de ficar com a tela de espera parada.
        if (w) {
          w.document.open();
          w.document.write(`<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
<title>Não foi possível gerar</title></head><body style="min-height:100vh;display:flex;
align-items:center;justify-content:center;background:#F2EDE9;color:#1A1714;
font-family:'Manrope','Segoe UI',system-ui,sans-serif;text-align:center;padding:32px">
<div><div style="color:#8A6244;letter-spacing:.34em;font-size:15px;font-weight:600">ATRIO</div>
<h1 style="font-size:16px;margin:24px 0 8px">Não foi possível gerar o pacote</h1>
<p style="font-size:13px;color:#5A554E;max-width:420px">${msg.replace(/</g, '&lt;')}</p>
<p style="font-size:12px;color:#8A847D;margin-top:18px">Feche esta aba e tente de novo.
Se persistir, avise o escritório.</p></div></body></html>`);
          w.document.close();
        }
      })
      .finally(() => setGerandoPacote(false));
  };
  const [dtIni, setDtIni] = useState(`${ano}-${String(mes).padStart(2, '0')}-01`);
  const [dtFim, setDtFim] = useState(`${ano}-${String(mes).padStart(2, '0')}-${String(new Date(ano, mes, 0).getDate()).padStart(2, '0')}`);

  const [html, setHtml] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  // Grupo da composicao. Vazio = todos os que existirem no plano.
  const [grupoComp, setGrupoComp] = useState('');
  // Uma conta so, aberta do balancete: { id, codigo } ou null.
  const [contaComp, setContaComp] = useState(null);
  // De onde o contador veio, quando chegou aqui por um botao e nao pelo menu.
  // Sem isso ele cai na composicao e so volta procurando o relatorio na barra.
  const [voltarPara, setVoltarPara] = useState(null);
  const [razao, setRazao] = useState(null);            // dados do razão da conta
  const [razaoLoading, setRazaoLoading] = useState(false);
  const [contas, setContas] = useState([]);            // contas com movimento (seletor do Razão)
  const [contaId, setContaId] = useState('');          // '' = todas
  const [msgSnapshot, setMsgSnapshot] = useState(null);

  // Carrega o HTML quando muda qualquer parâmetro
  useEffect(() => {
    if (!clienteId) return;
    const params = { ano: filtroAno, mes: filtroMes };
    if (encerramento) params.encerramento = encerramento;
    if (rel === 'dre-mensal') params.ate_mes = filtroMes;
    if (rel === 'bp-mensal') { params.ate_mes = filtroMes; params.modo = modoBp; }
    if (rel === 'balancete-mensal') { params.ate_mes = filtroMes; params.modo = modoBp; }
    // Conta zerada nos doze meses so afasta as colunas que importam.
    if (COM_LINHAS_VAZIAS.has(rel) && linhasModo === 'todas') params.linhas = 'todas';
    if (rel === 'balancete-mensal' && pctMensal !== 'nao') params.percentuais = pctMensal;
    if (!SEM_TIPO.has(rel)) params.tipo = tipo;
    if (tipo === 'livre') { params.data_inicio = dtIni; params.data_fim = dtFim; }
    if (!SEM_COMPARACAO.has(rel) && comparar) params.comparar = comparar;
    if (rel === 'razao' && contaId) params.conta_id = contaId;
    if (rel === 'composicao' && grupoComp) params.grupo = grupoComp;
    if (rel === 'composicao' && contaComp) params.conta_id = contaComp.id;
    if (rel === 'balancete' && codigoModo !== 'completo') params.codigo = codigoModo;
    if (COM_ASSINATURA.has(rel) && assinaturas !== 'ambas') params.assinaturas = assinaturas;
    if (rel === 'razao') params.origem = origemCol;
    // O backend já mostra AV%/AH% no relatório direto; só mandamos para desligar.
    if (COM_PERCENTUAIS.has(rel) && percentuaisModo === 'nao') params.percentuais = 'nao';
    ultimosParams.current = params;
    const url = urlHtmlRelatorio(rel, clienteId, params);
    setCarregando(true);
    setErro(null);
    carregarHtmlAutenticado(url)
      .then(setHtml)
      .catch(err => setErro(err.message))
      .finally(() => setCarregando(false));
  }, [clienteId, rel, filtroAno, filtroMes, tipo, comparar, contaId, dtIni, dtFim, modoBp,
      encerramento, codigoModo, assinaturas, origemCol, percentuaisModo, grupoComp,
      contaComp, linhasModo, pctMensal]);

  // Seletor de contas do Razão
  useEffect(() => {
    if (!clienteId || rel !== 'razao') return;
    razaoContas(clienteId, { ano: filtroAno, mes: filtroMes, tipo })
      .then(r => setContas(r.items || []))
      .catch(() => setContas([]));
  }, [clienteId, rel, filtroAno, filtroMes, tipo]);

  // Drill-down razão: escuta postMessage do iframe do balancete
  useEffect(() => {
    function onMessage(evt) {
      // O balancete manda abrir a composicao do grupo: o contador sai da
      // linha sintetica direto para o detalhe que precisa tratar.
      if (evt.data?.type === 'balancete:abrir-composicao') {
        setVoltarPara({ rel, rotulo: RELATORIOS.find(r => r.id === rel)?.label || 'relatório' });
        if (evt.data.contaId) {
          setContaComp({ id: evt.data.contaId, codigo: evt.data.codigo || '' });
          setGrupoComp('');
        } else {
          setContaComp(null);
          setGrupoComp(evt.data.grupo || '');
        }
        setRel('composicao');
        return;
      }
      // A composicao manda abrir o razao do titular: trata sem trocar de tela.
      const tipos = ['balancete:abrir-razao', 'composicao:abrir-razao'];
      if (!tipos.includes(evt.data?.type)) return;
      const { contaId } = evt.data;
      if (!contaId || !clienteId) return;
      setRazaoLoading(true);
      setRazao({ loading: true });
      const url = `${API_HOST}/relatorios/razao/${clienteId}/${contaId}?ano=${filtroAno}&mes=${filtroMes}&tipo=${tipo}`;
      // A consulta viaja com o razao: o PDF tem que sair igual a tela.
      const consulta = { ano: filtroAno, mes: filtroMes, tipo, conta_id: contaId };
      fetch(url, { headers: authHeaders() })
        .then(r => r.json())
        .then(data => setRazao({ ...data, consulta }))
        .catch(err => setRazao({ erro: err.message }))
        .finally(() => setRazaoLoading(false));
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [clienteId, filtroAno, filtroMes, tipo, rel]);

  // Fica AQUI, depois de `carregando` existir: as dependencias do efeito sao
  // lidas no render, e um const lido antes da declaracao derruba o app inteiro.
  // A folha tem 210mm = 793,7px a 96dpi. Para enche-la na largura disponivel,
  // o zoom e a razao entre as duas — com uma folga para a barra de rolagem
  // vertical nao provocar uma horizontal.
  const zoomDeLargura = () => {
    const w = molduraRef.current?.clientWidth;
    if (!w) return null;
    return Math.min(200, Math.max(50, Math.round(((w - 28) / 793.7) * 100)));
  };
  // Refaz a conta quando a largura muda: recolher o menu, redimensionar a
  // janela, abrir a lateral. Sem isto "ajustar" so valeria no instante em que
  // foi clicado.
  useEffect(() => {
    if (zoomModo !== 'largura') return;
    const recalcula = () => { const z = zoomDeLargura(); if (z) setZoom(z); };
    recalcula();
    const alvo = molduraRef.current;
    if (!alvo || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', recalcula);
      return () => window.removeEventListener('resize', recalcula);
    }
    const obs = new ResizeObserver(recalcula);
    obs.observe(alvo);
    return () => obs.disconnect();
  }, [zoomModo, rel, carregando]);

  if (!clienteId) return <Empty title="Selecione um cliente" />;

  // Parametros do ultimo relatorio carregado. O PDF e o mesmo HTML: mesma
  // consulta, sem recalcular nada aqui.
  const ultimosParams = useRef({});
  const [baixando, setBaixando] = useState(false);

  // Baixa um arquivo autenticado. Abrir o /pdf numa aba nao serve: a aba nova
  // nao leva o token, entao busca com o cabecalho e entrega por <a download>.
  const baixarArquivo = async (url, nomePadrao) => {
    setBaixando(true);
    setErro(null);
    try {
      const r = await fetch(url, { headers: authHeaders() });
      if (!r.ok) throw new Error((await r.text()).slice(0, 200) || `HTTP ${r.status}`);
      const disp = r.headers.get('content-disposition') || '';
      const m = /filename="([^"]+)"/.exec(disp);
      const blob = await r.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href; a.download = (m && m[1]) || nomePadrao;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    } catch (e) {
      setErro('Nao foi possivel gerar o PDF: ' + e.message);
    } finally {
      setBaixando(false);
    }
  };

  const baixarPdf = () => {
    if (!clienteId) return;
    baixarArquivo(urlPdfRelatorio(rel, clienteId, ultimosParams.current), `${rel}-${clienteId}.pdf`);
  };

  // Pra abrir em nova aba: monta uma data URL ou abre no app standalone
  const abrirNovaAba = () => {
    if (!html) return;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    window.open(URL.createObjectURL(blob), '_blank');
  };

  const congelar = async () => {
    try {
      const r = await criarSnapshot(clienteId, {
        relatorio: SNAPSHOT_TIPO[rel], ano: filtroAno, mes: filtroMes,
        tipo: SEM_TIPO.has(rel) ? 'mensal' : tipo,
      });
      setMsgSnapshot({ tipo: 'success', txt: `Versão congelada (#${r.id}). Libere ao cliente em Fechamento → Entregas.` });
    } catch (err) {
      setMsgSnapshot({ tipo: 'danger', txt: err.message });
    }
  };

  return (
    <>
      <div className="flex mb-2" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <b style={{ fontSize: 15, marginRight: 4, whiteSpace: "nowrap" }}>Relatórios contábeis</b>
        {RELATORIOS.map(r => (
          <button key={r.id} className={rel === r.id ? 'primary' : ''}
            onClick={() => { setVoltarPara(null); setRel(r.id); }}>
            {r.icon} {r.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
          <div className="flex" style={{ gap: 8 }}>
            <div ref={pacoteRef} style={{ position: 'relative' }}>
              <button onClick={() => { setPacoteAberto(v => !v); setOpcoesAberto(false); }}
                title="Documento unico para enviar ao cliente: escolha o conteudo">
                {'\u2709'} Pacote do cliente {pacoteAberto ? '\u25b4' : '\u25be'}
              </button>
              {pacoteAberto && (
                <div className="card" style={{ position: 'absolute', left: 0, top: '115%', zIndex: 40, minWidth: 290, width: 'max-content', maxWidth: 340, padding: 14, textAlign: 'left', boxShadow: '0 10px 30px rgba(0,0,0,.18)' }}>
                  <div className="small muted" style={{ marginBottom: 8 }}>O que vai no documento:</div>
                  {PACOTE_ITENS.map(([k, rotulo]) => (
                    <label key={k} style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-start', padding: '3px 0', cursor: 'pointer', fontSize: 13, textAlign: 'left', whiteSpace: 'nowrap' }}>
                      <input type="checkbox" checked={pacoteSel.includes(k)}
                        onChange={() => setPacoteSel(sel => sel.includes(k) ? sel.filter(x => x !== k) : [...sel, k])} />
                      {rotulo}
                    </label>
                  ))}
                  <button style={{ marginTop: 8, width: '100%' }}
                    onClick={baixarPacotePdf} disabled={!pacoteSel.length || baixando}
                    title="Gera o PDF no servidor e baixa. Nao depende da impressao do navegador.">
                    {baixando ? 'Gerando o PDF…' : '⬇ Baixar PDF do pacote'}
                  </button>
                  <button className="primary" style={{ marginTop: 10, width: '100%' }}
                    onClick={abrirPacote} disabled={!pacoteSel.length || gerandoPacote}>
                    {gerandoPacote ? 'Gerando… aguarde' : 'Gerar pacote'}
                  </button>
                  {gerandoPacote && (
                    <div className="small muted" style={{ marginTop: 6, textAlign: 'center' }}>
                      Abrimos uma aba com o andamento
                    </div>
                  )}
                  <button style={{ marginTop: 8, width: '100%' }} onClick={salvarPadrao}
                    title="Guarda esta seleção como seu padrão, para qualquer cliente">
                    {padraoSalvo ? 'Padrão salvo ✓' : 'Salvar seleção como meu padrão'}
                  </button>
                </div>
              )}
            </div>
            {TEM_OPCOES[rel] && (
              <div ref={opcoesRef} style={{ position: 'relative' }}>
                <button onClick={() => { setOpcoesAberto(v => !v); setPacoteAberto(false); }}
                  className={OPCOES_ATIVAS() ? 'primary' : ''}
                  title="Como o relatório sai na impressão">
                  {'\u2699'} Impressão{OPCOES_ATIVAS() ? ` (${OPCOES_ATIVAS()})` : ''} {opcoesAberto ? '\u25b4' : '\u25be'}
                </button>
                {opcoesAberto && (
                  <div className="card" style={{ position: 'absolute', left: 0, top: '115%', zIndex: 40, minWidth: 290, width: 'max-content', maxWidth: 340, padding: 14, textAlign: 'left', boxShadow: '0 10px 30px rgba(0,0,0,.18)' }}>
                    <div className="small muted" style={{ marginBottom: 8 }}>Como sai na impressão:</div>
                    {rel === 'balancete' && (<>
                      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', justifyContent: 'flex-start', padding: '4px 0', cursor: 'pointer', fontSize: 13, textAlign: 'left' }}>
                        <input type="checkbox" style={{ marginTop: 3 }}
                          checked={codigoModo === 'reduzido'}
                          onChange={e => setCodigoModo(e.target.checked ? 'reduzido' : 'completo')} />
                        <span>Só o código reduzido
                          <span className="small muted" style={{ display: 'block' }}>Oculta a classificação; o nome da conta para de quebrar</span>
                        </span>
                      </label>
                    </>)}
                    {COM_ASSINATURA.has(rel) && (
                      <div style={{ padding: '4px 0', fontSize: 13, textAlign: 'left' }}>
                        <div style={{ marginBottom: 4 }}>Assinaturas</div>
                        <select value={assinaturas} onChange={e => setAssinaturas(e.target.value)}
                          style={{ width: '100%' }}>
                          <option value="ambas">Empresa e contador</option>
                          <option value="contador">Só o contador</option>
                          <option value="nenhuma">Sem assinatura</option>
                        </select>
                        <span className="small muted" style={{ display: 'block', marginTop: 3 }}>
                          {assinaturas === 'nenhuma'
                            ? 'Via de conferência: o documento que vai a órgão leva as assinaturas'
                            : assinaturas === 'contador'
                              ? 'Sem o campo do responsável pela empresa'
                              : 'Campo para a empresa e para o contador'}
                        </span>
                      </div>
                    )}
                    {rel === 'razao' && (
                      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', justifyContent: 'flex-start', padding: '4px 0', cursor: 'pointer', fontSize: 13, textAlign: 'left' }}>
                        <input type="checkbox" style={{ marginTop: 3 }}
                          checked={origemCol === 'sim'}
                          onChange={e => setOrigemCol(e.target.checked ? 'sim' : 'nao')} />
                        <span>Mostrar a coluna Origem
                          <span className="small muted" style={{ display: 'block' }}>Manual, NFS-e, Finance: controle interno, fica fora do relatório do cliente</span>
                        </span>
                      </label>
                    )}
                    {OPCOES_ATIVAS() > 0 && (
                      <button style={{ marginTop: 10, width: '100%' }} onClick={RESTAURAR_PADRAO}>
                        {prefs ? 'Voltar ao meu padrão' : 'Voltar ao padrão'}
                      </button>
                    )}
                    <button className="primary" style={{ marginTop: 8, width: '100%' }} onClick={salvarPadrao}
                      title="Vale para você em qualquer cliente e em qualquer máquina">
                      {padraoSalvo ? 'Padrão salvo ✓' : 'Salvar como meu padrão'}
                    </button>
                    <a href="#/configuracoes" className="small muted"
                       style={{ display: 'block', marginTop: 8, textAlign: 'center' }}>
                      Ver todas as configurações
                    </a>
                  </div>
                )}
              </div>
            )}
            {SNAPSHOT_TIPO[rel] && (
              <button onClick={congelar} disabled={!html} title="Congela a versão atual para liberar ao cliente">
                ❄ Congelar versão
              </button>
            )}
            <button onClick={baixarPdf} disabled={!html || baixando}
              title="Gera o PDF no servidor e baixa. Nao depende da impressao do navegador.">
              {baixando ? 'Gerando o PDF…' : '⬇ Baixar PDF'}
            </button>
            <button className="primary" onClick={abrirNovaAba} disabled={!html}>
              ⎘ Abrir em nova aba
            </button>
          </div>
      </div>

      {msgSnapshot && <Alert tipo={msgSnapshot.tipo}>{msgSnapshot.txt}</Alert>}


      <div className="toolbar">
        <select value={encerramento} onChange={e => setEncerramento(e.target.value)}
          title="Antes do encerramento mostra as contas de resultado abertas; depois, o resultado já incorporado ao patrimônio líquido">
          <option value="">Encerramento: padrão do relatório</option>
          <option value="sem">Antes do encerramento</option>
          <option value="com">Depois do encerramento</option>
        </select>
        {tipo !== 'livre' && (
        <select value={filtroAno} onChange={e => setFiltroAno(Number(e.target.value))}>
          {anosOpcoes(5).map(a => <option key={a} value={a}>{a}</option>)}
        </select>)}
        {tipo !== 'livre' && (
        <select value={filtroMes} onChange={e => setFiltroMes(Number(e.target.value))}>
          {mesOpcoes().map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>)}
        {!SEM_TIPO.has(rel) && (
          <select value={tipo} onChange={e => setTipo(e.target.value)}>
            {TIPOS.filter(t => !t.soDre || rel === 'dre')
                  .map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        )}
        {(rel === 'bp-mensal' || rel === 'balancete-mensal') && (
          <select value={modoBp} onChange={e => setModoBp(e.target.value)}
            title="Saldo = posição no fim do mês · Movimento = variação do mês">
            <option value="saldo">Saldo no fim do mês</option>
            <option value="movimento">Movimento do mês</option>
            {rel === 'balancete-mensal' && (
              <option value="misto">Patrimônio pelo saldo, resultado pelo movimento</option>
            )}
          </select>
        )}
        {!SEM_TIPO.has(rel) && tipo === 'livre' && (
          <>
            <input type="date" value={dtIni} onChange={e => setDtIni(e.target.value)}
              title="Data inicial" style={{ padding: '7px 10px', borderRadius: 8 }} />
            <input type="date" value={dtFim} onChange={e => setDtFim(e.target.value)}
              title="Data final" style={{ padding: '7px 10px', borderRadius: 8 }} />
          </>
        )}
        {!SEM_COMPARACAO.has(rel) && (
          <select value={comparar} onChange={e => setComparar(e.target.value)}
            title="A demonstração comparativa é a regra (CPC 26 R1, item 38, e art. 176, § 1º, da Lei 6.404/76)">
            <option value="">{
              tipo === 'ultimos_12' ? 'Comparativo: 12 meses anteriores (padrão)'
              : rel === 'dre' && ['mensal', 'trimestral', 'semestral'].includes(tipo)
                ? 'Comparativo: mesmo período do ano anterior (padrão)'
                : 'Comparativo: exercício anterior (padrão)'}</option>
            <option value="exercicio_anterior">
              {rel === 'balanco' ? 'Vs 31/12 do exercício anterior' : 'Vs exercício anterior inteiro'}
            </option>
            <option value="ano_anterior">Vs mesmo período do ano anterior</option>
            <option value="mes_anterior">Vs mês anterior</option>
            <option value="sem">Sem comparativo</option>
          </select>
        )}
        {voltarPara && (
          <button onClick={() => {
            setRel(voltarPara.rel);
            setContaComp(null);
            setGrupoComp('');
            setVoltarPara(null);
          }} title="Volta ao relatório de onde você abriu esta composição">
            ← Voltar ao {voltarPara.rotulo}
          </button>
        )}
        {rel === 'composicao' && (
          <select value={contaComp ? 'conta' : grupoComp}
            onChange={e => { setContaComp(null); setGrupoComp(e.target.value === 'conta' ? '' : e.target.value); }}
            style={{ minWidth: 240 }}
            title="A composição é a posição na data final do recorte">
            {contaComp && <option value="conta">Conta {contaComp.codigo}</option>}
            <option value="">Todos os grupos de terceiros</option>
            {GRUPOS_COMPOSICAO.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
        )}
        {rel === 'razao' && (
          <select value={contaId} onChange={e => setContaId(e.target.value)} style={{ minWidth: 260 }}>
            <option value="">Todas as contas com movimento ({contas.length})</option>
            {contas.map(c => (
              <option key={c.id} value={c.id}>{c.codigo} — {c.nome} ({c.qtd_lctos})</option>
            ))}
          </select>
        )}
        {rel === 'balancete-mensal' && (
          <select value={pctMensal} onChange={e => setPctMensal(e.target.value)}
            title="Leitura de tela: os percentuais não saem na impressão">
            <option value="nao">Sem percentuais</option>
            <option value="ah">AH%: variação sobre o mês anterior</option>
            <option value="av">AV%: peso sobre o total do grupo</option>
          </select>
        )}
        {COM_LINHAS_VAZIAS.has(rel) && (
          <select value={linhasModo} onChange={e => setLinhasModo(e.target.value)}
            title="Conta zerada nos doze meses não informa nada e só afasta as colunas que importam">
            <option value="movimento">Só contas com movimento</option>
            <option value="todas">Todas as contas do plano</option>
          </select>
        )}
        {COM_PERCENTUAIS.has(rel) && (
          <button className={percentuaisModo === 'sim' ? 'primary' : ''}
            onClick={() => setPercentuaisModo(m => (m === 'sim' ? 'nao' : 'sim'))}
            title="Análise vertical (AV%: peso sobre o total) e horizontal (AH%: variação sobre o comparativo). Só na tela — a impressão e o pacote nunca levam estas colunas.">
            AV% · AH%
          </button>
        )}
        <div className="flex" style={{ marginLeft: 'auto', alignItems: 'center', gap: 2 }}
          title="Zoom do relatório na tela — não afeta a impressão">
          <button className="ghost" style={BTN_ZOOM} onClick={() => ajustarZoom(zoom - 10)}
            disabled={zoom <= 50} aria-label="Diminuir zoom">−</button>
          <button className="ghost" onClick={() => ajustarZoom(100)}
            style={{ ...BTN_ZOOM, width: 54, fontVariantNumeric: 'tabular-nums',
                     fontWeight: zoom === 100 ? 500 : 700 }}
            title="Voltar a 100% (tamanho de impressão)">{zoom}%</button>
          <button className="ghost" style={BTN_ZOOM} onClick={() => ajustarZoom(zoom + 10)}
            disabled={zoom >= 200} aria-label="Aumentar zoom">+</button>
          <button className="ghost" onClick={() => gravarModo('largura')}
            style={{ ...BTN_ZOOM, marginLeft: 4,
                     color: zoomModo === 'largura' ? 'var(--gold)' : undefined,
                     borderColor: zoomModo === 'largura' ? 'var(--gold)' : undefined }}
            title="Ajustar à largura — a folha preenche o espaço e o texto cresce junto">⇔</button>
        </div>
      </div>

      <AvisoForaDaCarteira clienteId={clienteId} ano={filtroAno} mes={filtroMes}
        acao="Este relatório" />

      {erro && <Alert tipo="danger">{erro}</Alert>}

      <div className="card" ref={molduraRef} style={{ padding: 0, overflow: 'hidden', minHeight: 200 }}>
        {carregando ? <Loading texto="Gerando relatório…" /> : (
          <iframe
            srcDoc={comZoom(html, zoom)}
            title={rel}
            style={{ width: '100%', height: 'calc(100vh - 280px)', border: 'none', display: 'block',
              // Clique dentro de um iframe não borbulha para o documento pai, então
              // com um popover aberto ele deixa de capturar: o clique cai no cartão,
              // sobe até o listener de mousedown e fecha.
              pointerEvents: (pacoteAberto || opcoesAberto) ? 'none' : 'auto' }}
          />
        )}
      </div>

      {razao && <RazaoModal razao={razao} loading={razaoLoading} clienteId={clienteId} contas={contas}
          baixando={baixando} erroPdf={erro}
          onBaixarPdf={razao.consulta ? () => baixarArquivo(
            urlPdfRelatorio('razao', clienteId, razao.consulta),
            `razao-${clienteId}-conta-${razao.consulta.conta_id}.pdf`) : null}
          onFechar={() => setRazao(null)} onSalvo={() => setRazao(null)} />}
    </>
  );
}

// =============================================================================
// Modal: Razão da Conta — drill-down do Balancete
// =============================================================================
function RazaoModal({ razao, loading, onFechar, clienteId, contas = [], onSalvo,
                     onBaixarPdf = null, baixando = false, erroPdf = null }) {
  const [editando, setEditando] = useState(null);   // lcto_id em edicao
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(8,8,10,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
      padding: 20,
    }} onClick={onFechar}>
      <div className="card" style={{
        width: '90%', maxWidth: 1100, maxHeight: '90vh',
        overflow: 'auto', padding: 20,
      }} onClick={e => e.stopPropagation()}>
        {loading || razao.loading ? <Loading texto="Carregando razão…" /> : razao.erro ? (
          <Alert tipo="danger">{razao.erro}</Alert>
        ) : (
          <>
            <div className="flex mb-2" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: 0, color: 'var(--gold)' }}>
                  Razão da Conta
                </h2>
                <div className="small muted" style={{ fontFamily: 'var(--font-data)', marginTop: 4 }}>
                  {razao.conta.codigo} · {razao.conta.nome} · {razao.competencia}
                </div>
              </div>
              {onBaixarPdf && (
                <button onClick={onBaixarPdf} disabled={baixando} style={{ marginRight: 8 }}>
                  {baixando ? 'Gerando o PDF…' : '⬇ Baixar PDF'}
                </button>
              )}
              <button onClick={onFechar}>Fechar</button>
            </div>
            {erroPdf && <Alert tipo="danger">{erroPdf}</Alert>}

            <div className="flex" style={{ gap: 16, marginBottom: 12, fontFamily: 'var(--font-data)' }}>
              <div>
                <div className="small muted">Saldo Anterior</div>
                <div style={{ fontSize: 16 }}>
                  {fmtMoeda(razao.saldo_anterior.valor)} {razao.saldo_anterior.valor > 0 && (
                    <span className="badge badge--neutral" style={{ fontSize: 10 }}>{razao.saldo_anterior.natureza}</span>
                  )}
                </div>
              </div>
              <div>
                <div className="small muted">Débito do período</div>
                <div style={{ fontSize: 16, color: 'var(--gold)' }}>{fmtMoeda(razao.totais.debito)}</div>
              </div>
              <div>
                <div className="small muted">Crédito do período</div>
                <div style={{ fontSize: 16, color: '#5E748C' }}>{fmtMoeda(razao.totais.credito)}</div>
              </div>
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div className="small muted">Saldo Atual</div>
                <div style={{ fontSize: 20, fontWeight: 600 }}>
                  {fmtMoeda(razao.totais.saldo_final.valor)}{' '}
                  <span className="badge badge--neutral" style={{ fontSize: 10 }}>{razao.totais.saldo_final.natureza}</span>
                </div>
              </div>
            </div>

            {razao.linhas.length === 0 ? (
              <div className="muted small" style={{ padding: 20, textAlign: 'center' }}>
                Sem movimento no período pra essa conta.
              </div>
            ) : (
              <table className="data" style={{ width: '100%', fontFamily: 'var(--font-data)' }}>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Lcto</th>
                    <th>Histórico</th>
                    <th>Contrapartida</th>
                    <th>Origem</th>
                    <th style={{ textAlign: 'right' }}>Débito</th>
                    <th style={{ textAlign: 'right' }}>Crédito</th>
                    <th style={{ textAlign: 'right' }}>Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {razao.linhas.map(l => (
                    <tr key={l.lcto_id} onClick={() => clienteId && setEditando(l.lcto_id)}
                        title={clienteId ? 'Clique para corrigir este lançamento' : ''}
                        style={{ cursor: clienteId ? 'pointer' : 'default' }}>
                      <td>{fmtData(l.data)}</td>
                      <td>#{l.numero || l.lcto_id}</td>
                      <td className="small">{l.historico}</td>
                      <td className="small" style={{ color: 'var(--gold)', maxWidth: 260 }}>{l.contrapartida}</td>
                      <td><span className="badge badge--neutral" style={{ fontSize: 9 }}>{l.origem}</span></td>
                      <td style={{ textAlign: 'right', color: l.debito ? 'var(--gold)' : 'var(--text-3)' }}>
                        {l.debito ? fmtMoeda(l.debito) : '—'}
                      </td>
                      <td style={{ textAlign: 'right', color: l.credito ? '#5E748C' : 'var(--text-3)' }}>
                        {l.credito ? fmtMoeda(l.credito) : '—'}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoeda(l.saldo)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--gold)' }}>
                    <td colSpan="4" style={{ fontWeight: 600 }}>TOTAIS ({razao.totais.qtd_lctos} lançamentos)</td>
                    <td style={{ textAlign: 'right', color: 'var(--gold)', fontWeight: 600 }}>{fmtMoeda(razao.totais.debito)}</td>
                    <td style={{ textAlign: 'right', color: '#5E748C', fontWeight: 600 }}>{fmtMoeda(razao.totais.credito)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                      {fmtMoeda(razao.totais.saldo_final.valor)} {razao.totais.saldo_final.natureza}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </>
        )}
        {editando && (
          <EditorPartidas clienteId={clienteId} lctoId={editando}
            onFechar={() => setEditando(null)}
            onSalvo={() => { setEditando(null); onSalvo && onSalvo(); }} />
        )}
      </div>
    </div>
  );
}


// =============================================================================
// Editor de lancamento — abre pelo razao e altera o proprio lancamento: data,
// historico e partidas. Nao ha estorno nem substituto; o que limita a alteracao
// e o periodo, que precisa estar aberto.
// =============================================================================
function EditorPartidas({ clienteId, lctoId, onFechar, onSalvo }) {
  const [lcto, setLcto] = useState(null);
  const [partidas, setPartidas] = useState([]);
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [plano, setPlano] = useState([]);   // plano completo, nao so as contas com movimento

  // o razao pode ser aberto de qualquer aba, entao o editor busca o plano ele mesmo
  useEffect(() => {
    // o endpoint ja devolve so as contas ativas; tipo='A' filtra as analiticas
    planoCliente(clienteId, { tipo: 'A' })
      .then(r => setPlano(r.items || r || []))
      .catch(() => setPlano([]));
  }, [clienteId]);

  useEffect(() => {
    obterLancamento(clienteId, lctoId)
      .then(l => {
        setLcto(l);
        setPartidas((l.partidas || []).map(p => ({
          lado: p.lado, conta_id: p.conta_id, conta_codigo: p.conta_codigo,
          conta_nome: p.conta_nome, valor: Number(p.valor),
          historico_compl: p.historico_compl || '',
        })));
      })
      .catch(e => setErro(e.message));
  }, [clienteId, lctoId]);

  const mudar = (i, campo, valor) =>
    setPartidas(ps => ps.map((p, k) => (k === i ? { ...p, [campo]: valor } : p)));
  const inverterTudo = () =>
    setPartidas(ps => ps.map(p => ({ ...p, lado: p.lado === 'D' ? 'C' : 'D' })));

  const totD = partidas.filter(p => p.lado === 'D').reduce((s, p) => s + Number(p.valor || 0), 0);
  const totC = partidas.filter(p => p.lado === 'C').reduce((s, p) => s + Number(p.valor || 0), 0);
  const fecha = Math.abs(totD - totC) < 0.005;

  const excluir = async () => {
    setErro(null);
    if (!confirm(`Excluir o lançamento #${lctoId}? Enquanto o período está aberto ele sai de vez.`)) return;
    setSalvando(true);
    try {
      await excluirLancamento(clienteId, lctoId);
      onSalvo();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const salvar = async () => {
    setErro(null);
    if (!fecha) return setErro('Débito e crédito não fecham.');
    if (!String(lcto.historico || '').trim()) return setErro('O histórico não pode ficar vazio.');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(lcto.data_lcto).slice(0, 10))) {
      return setErro('Data do lançamento inválida.');
    }
    setSalvando(true);
    try {
      // origem_tipo e origem_ref vao de volta como estavam: sem eles a rota
      // aplica o padrao 'manual' e um lancamento de NFS-e perderia a origem.
      await atualizarLancamento(clienteId, lctoId, {
        data_lcto: String(lcto.data_lcto).slice(0, 10),
        historico: String(lcto.historico).trim(),
        origem_tipo: lcto.origem_tipo || 'manual',
        origem_ref: lcto.origem_ref ?? null,
        partidas: partidas.map(p => ({
          lado: p.lado, conta_id: Number(p.conta_id), valor: Number(p.valor),
          // sem isto salvar apagava o historico complementar de cada partida
          historico_compl: p.historico_compl || undefined,
        })),
      });
      onSalvo();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  // O overlay vai para o body. A classe .card anima com `rise` em fill-mode
  // both, e a animação termina aplicando um transform. Transform num ancestral
  // vira bloco de contenção do `position: fixed`, então o editor se ancorava no
  // cartão do razão e rolava junto com a lista em vez de ficar preso na tela.
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(8,8,10,.8)', zIndex: 300,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
         onClick={onFechar}>
      <div className="card" style={{ width: '90%', maxWidth: 760, maxHeight: '88vh',
                                     overflow: 'auto', padding: 20 }}
           onClick={e => e.stopPropagation()}>
        <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <b>Editar lançamento #{lctoId}</b>
          <button onClick={onFechar}>Fechar</button>
        </div>
        {erro && <div className="small" style={{ color: 'var(--danger, #c33)', margin: '8px 0' }}>{erro}</div>}
        {!lcto ? <div className="small muted" style={{ padding: 16 }}>Carregando…</div> : (
          <>
            <div className="flex" style={{ gap: 8, margin: '6px 0 12px', alignItems: 'flex-end' }}>
              <label className="small muted" style={{ display: 'block' }}>
                Data
                <input type="date" value={String(lcto.data_lcto).slice(0, 10)}
                       onChange={e => setLcto(l => ({ ...l, data_lcto: e.target.value }))}
                       style={{ display: 'block', marginTop: 3, padding: '6px 8px', borderRadius: 8 }} />
              </label>
              <label className="small muted" style={{ display: 'block', flex: 1 }}>
                Histórico
                <input type="text" value={lcto.historico || ''}
                       onChange={e => setLcto(l => ({ ...l, historico: e.target.value }))}
                       placeholder="O que o lançamento registra"
                       style={{ display: 'block', marginTop: 3, width: '100%', padding: '6px 8px', borderRadius: 8 }} />
              </label>
            </div>
            <div className="flex mb-2" style={{ gap: 8 }}>
              <button onClick={inverterTudo}>⇅ Inverter débito e crédito</button>
            </div>
            <table style={{ width: '100%' }}>
              <thead>
                <tr><th style={{ width: 70 }}>Lado</th><th>Conta</th>
                    <th style={{ textAlign: 'right', width: 140 }}>Valor</th></tr>
              </thead>
              <tbody>
                {partidas.map((p, i) => (
                  <tr key={i}>
                    <td>
                      <select value={p.lado} onChange={e => mudar(i, 'lado', e.target.value)}>
                        <option value="D">D</option><option value="C">C</option>
                      </select>
                    </td>
                    <td>
                      <select value={p.conta_id} style={{ width: '100%' }}
                              onChange={e => mudar(i, 'conta_id', e.target.value)}>
                        {!plano.some(c => String(c.id) === String(p.conta_id)) && (
                          <option value={p.conta_id}>{p.conta_codigo} · {p.conta_nome}</option>
                        )}
                        {plano.map(c => (
                          <option key={c.id} value={c.id}>{c.codigo} · {c.nome}</option>
                        ))}
                      </select>
                      {!plano.length && (
                        <div className="small muted">carregando o plano de contas…</div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <input type="number" step="0.01" value={p.valor} style={{ width: 130, textAlign: 'right' }}
                             onChange={e => mudar(i, 'valor', e.target.value)} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--gold)' }}>
                  <td colSpan="2" style={{ fontWeight: 600 }}>
                    {fecha ? 'Partida dobrada fecha' : 'Débito e crédito não fecham'}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>
                    D {fmtMoeda(totD)} · C {fmtMoeda(totC)}
                  </td>
                </tr>
              </tfoot>
            </table>
            <div className="small muted" style={{ marginTop: 10 }}>
              A alteração é gravada no próprio lançamento. Fechado o período, ele deixa de
              aceitar mudança: reabra o mês, o que fica registrado com autor e motivo.
            </div>
            <div className="flex" style={{ gap: 8, marginTop: 12 }}>
              <button className="primary" style={{ flex: 1 }}
                      onClick={salvar} disabled={salvando || !fecha}>
                {salvando ? 'Gravando…' : 'Salvar alterações'}
              </button>
              <button onClick={excluir} disabled={salvando}
                      title="Exclui o lançamento. Só funciona com o período aberto."
                      style={{ color: '#c0392b' }}>
                ⌫ Excluir lançamento
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
